<template>
  <v-container fluid class="mt-10 px-8">
    <v-row align="center">
      <v-col cols="auto"><h1 class="font-weight-thin">QA Jira Import</h1></v-col>
      <v-col cols="auto">
        <v-btn text x-small color="primary" @click="showHelpDialog = true" data-cy="jira-qa-import__help-btn">
          <v-icon x-small class="mr-1" aria-hidden="true">info</v-icon>How does this work?
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog v-model="showHelpDialog" max-width="500">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-medium">How does this work?</v-card-title>
        <v-card-text class="pb-2">
          <div class="subtitle-2 mb-1">What appears here</div>
          <ul class="body-2 mb-4 pl-4">
            <li>Has a Jira status of <em>In Documentation</em></li>
            <li>Has <em>Continuum Release Note</em> in the Services Needed field</li>
            <li>Has no existing Continuum release note with a matching QA Jira</li>
          </ul>
          <div class="subtitle-2 mb-1">Auto-mapped from Jira</div>
          <p class="body-2 mb-4">Build Type, Release Type, UAT Environment, Operationalize Type, Care Setting, Other Activities Impacted, Build Jira, Training Jira, and analyst assignments (QA Analyst, Build Analyst, Support Analysts).</p>
          <div class="subtitle-2 mb-1">You confirm before creating</div>
          <p class="body-2 mb-0">Pack, Template Type, Application, Primary Activity, and Service Area Attributes. These are pre-filled where a match is found — review and adjust as needed.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showHelpDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <generic-loader caption="Loading QA Jira issues..." top-margin="10" v-if="isLoading"></generic-loader>

    <template v-else>
      <v-alert type="info" outlined v-if="!issueList.length">
        No QA Jira issues are currently awaiting documentation.
      </v-alert>

      <template v-else>
        <v-card elevation="0">
          <v-card-text>
            <v-data-table
              :custom-sort="customSort"
              :headers="headers"
              :items="filteredIssueList"
              class="jira-import-table"
              item-key="key"
              must-sort
              data-cy="jira-qa-import-table"
            >
              <template v-slot:top>
                <v-row class="mb-2" align="center">
                  <v-col cols="4">
                    <v-text-field
                      v-model="searchText"
                      clearable
                      dense
                      hide-details
                      label="Search"
                      outlined
                      prepend-inner-icon="search"
                      data-cy="jira-qa-import-table__search"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="auto">
                    <v-checkbox
                      v-model="showOnlyMine"
                      label="Show only mine"
                      hide-details
                      dense
                      data-cy="jira-qa-import-table__show-only-mine"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </template>

              <template v-slot:item="{ item }">
                <tr>
                  <td style="white-space: nowrap"><a :href="`https://jira.ochin.org/browse/${item.key}`" target="_blank" rel="noopener noreferrer" title="Opens in new tab">{{ item.key }}</a></td>
                  <td>{{ item.fields && item.fields.summary }}</td>
                  <td>{{ singleSelectValue(item.fields && item.fields.customfield_12314) }}</td><!-- buildType -->
                  <td>{{ singleSelectValue(item.fields && item.fields.customfield_12315) }}</td><!-- releaseType -->
                  <td>{{ singleSelectValue(item.fields && item.fields.customfield_16003) }}</td><!-- operationalizeType -->
                  <td>{{ multiSelectValue(item.fields && item.fields.customfield_18600) }}</td><!-- application -->
                  <td>{{ singleSelectValue(item.fields && item.fields.customfield_14802) }}</td><!-- primaryActivity -->
                  <td>{{ singleSelectValue(item.fields && item.fields.customfield_18700) }}</td><!-- activitiesImpacted -->
                  <td>
                    <template v-if="!item.resolvedAssignee"></template>
                    <template v-else-if="item.resolvedAssignee.found">{{ item.resolvedAssignee.displayName }}</template>
                    <template v-else>
                      <div class="d-flex align-center"><span><span class="sr-only">Unmatched: </span>{{ item.resolvedAssignee.displayName }}</span><v-tooltip bottom><template v-slot:activator="{ on }"><v-icon x-small color="warning" class="ml-1 flex-shrink-0" v-on="on" aria-hidden="true">fas fa-exclamation-triangle</v-icon></template><span>User could not be uniquely auto-mapped to a Continuum account</span></v-tooltip></div>
                    </template>
                  </td>
                  <td>
                    <template v-if="!item.resolvedReporter"></template>
                    <template v-else-if="item.resolvedReporter.found">{{ item.resolvedReporter.displayName }}</template>
                    <template v-else>
                      <div class="d-flex align-center"><span><span class="sr-only">Unmatched: </span>{{ item.resolvedReporter.displayName }}</span><v-tooltip bottom><template v-slot:activator="{ on }"><v-icon x-small color="warning" class="ml-1 flex-shrink-0" v-on="on" aria-hidden="true">fas fa-exclamation-triangle</v-icon></template><span>User could not be uniquely auto-mapped to a Continuum account</span></v-tooltip></div>
                    </template>
                  </td>
                  <td>
                    <template v-for="(watcher, i) in item.resolvedWatchers">
                      <span :key="i">
                        <span v-if="!watcher.found" class="d-inline-flex align-center"><span><span class="sr-only">Unmatched: </span>{{ watcher.displayName }}</span><v-tooltip bottom><template v-slot:activator="{ on }"><v-icon x-small color="warning" class="ml-1 flex-shrink-0" v-on="on" aria-hidden="true">fas fa-exclamation-triangle</v-icon></template><span>User could not be uniquely auto-mapped to a Continuum account</span></v-tooltip></span><template v-else>{{ watcher.displayName }}</template>
                      </span>
                      <span v-if="i < item.resolvedWatchers.length - 1" :key="`sep-${i}`">, </span>
                    </template>
                  </td>
                  <td>
                    <v-btn
                      @click="openCreatePage(item)"
                      color="primary"
                      outlined
                      x-small
                      data-cy="jira-qa-import-table__create-btn"
                    >Import</v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </template>
    </template>

  </v-container>
</template>

<script>
import { mapActions, mapState } from "vuex";
import GenericLoader from "../../components/GenericLoader";

export default {
  name: "JiraQaImportList",

  components: { GenericLoader },

  data() {
    return {
      headers: [
        { text: "QA Jira", value: "key", sortable: true, width: "70px" },
        { text: "Release Note Title (Jira Summary)", value: "fields.summary", sortable: true, width: "220px" },
        { text: "Build Type", value: "fields.customfield_12314", sortable: true },
        { text: "Release Type", value: "fields.customfield_12315", sortable: true },
        { text: "Operationalize Type", value: "fields.customfield_16003", sortable: true },
        { text: "Application", value: "fields.customfield_18600", sortable: true },
        { text: "Primary Activity", value: "fields.customfield_14802", sortable: true },
        { text: "Other Activities Impacted", value: "fields.customfield_18700", sortable: true },
        { text: "QA Analyst (Jira Assignee)", value: "resolvedAssignee", sortable: true, width: "130px" },
        { text: "Build Analyst (Jira Reporter)", value: "resolvedReporter", sortable: true, width: "130px" },
        { text: "Support Analysts (Jira Watchers)", value: "resolvedWatchers", sortable: true, width: "140px" },
        { text: "", value: "actions", sortable: false },
      ],
      searchText: "",
      showHelpDialog: false,
      showOnlyMine: false,
    };
  },

  computed: {
    filteredIssueList() {
      let list = this.issueList;
      if (this.showOnlyMine) {
        list = list.filter(issue =>
          issue.resolvedAssignee?.found && issue.resolvedAssignee.username === this.loggedInUsername
        );
      }
      const query = (this.searchText || "").trim().toLowerCase();
      if (!query) return list;
      return list.filter(issue => {
        const f = issue.fields || {};
        const terms = [
          issue.key,
          f.summary,
          f.customfield_12314?.value ?? f.customfield_12314, // buildType
          f.customfield_12315?.value ?? f.customfield_12315, // releaseType
          f.customfield_16003?.value ?? f.customfield_16003, // operationalizeType
          f.customfield_18700?.value ?? f.customfield_18700, // activitiesImpacted
          Array.isArray(f.customfield_18600) ? f.customfield_18600.map(i => i.value ?? i).join(" ") : f.customfield_18600?.value, // application
          f.customfield_14802?.value ?? f.customfield_14802, // primaryActivity
          issue.resolvedAssignee?.displayName,
          issue.resolvedReporter?.displayName,
          (issue.resolvedWatchers || []).map(w => w.displayName).join(" "),
        ];
        return terms.some(t => t && String(t).toLowerCase().includes(query));
      });
    },
    ...mapState({
      isLoading: state => state.jiraQaImport.isLoading,
      issueList: state => state.jiraQaImport.issueList,
      loggedInUsername: state => state.session.username,
    }),
  },

  async created() {
    try {
      await this.loadIssues();
    } catch (e) {
      this.$notifyError({ message: "Error loading QA Jira issues" });
    }
  },

  methods: {
    customSort(items, sortBy, sortDesc) {
      if (!sortBy.length) return items;
      const field = sortBy[0];
      const desc = sortDesc[0];

      const getValue = (item) => {
        switch (field) {
          case "fields.summary":
            return item.fields?.summary ?? "";
          case "fields.customfield_12314": // buildType
          case "fields.customfield_12315": // releaseType
          case "fields.customfield_16003": // operationalizeType
          case "fields.customfield_18700": // activitiesImpacted
          case "fields.customfield_14802": { // primaryActivity
            const raw = item.fields?.[field.replace("fields.", "")];
            return raw?.value ?? raw ?? "";
          }
          case "fields.customfield_18600": { // application (multi-select array)
            const raw = item.fields?.customfield_18600;
            return Array.isArray(raw) ? raw.map(i => i.value ?? i).join(", ") : "";
          }
          case "resolvedAssignee":
            return item.resolvedAssignee?.displayName ?? "";
          case "resolvedReporter":
            return item.resolvedReporter?.displayName ?? "";
          case "resolvedWatchers":
            return (item.resolvedWatchers || []).map(w => w.displayName).join(", ");
          default:
            return item[field] ?? "";
        }
      };

      return [...items].sort((a, b) => {
        const cmp = String(getValue(a)).localeCompare(String(getValue(b)));
        return desc ? -cmp : cmp;
      });
    },

    openCreatePage(issue) {
      this.$store.commit("jiraQaImport/setSelectedIssue", issue);
      this.$router.push({ name: "jiraQaImportCreate" });
    },

    multiSelectValue(field) {
      if (!Array.isArray(field)) return "";
      return field.map(item => item.value ?? item).filter(Boolean).join(", ");
    },

    singleSelectValue(field) {
      if (!field) return "";
      return field.value || field;
    },

    ...mapActions({
      loadIssues: "jiraQaImport/loadIssues",
    }),
  },
};
</script>

<style scoped>
::v-deep .jira-import-table td,
::v-deep .jira-import-table th {
  font-size: 0.75rem !important;
}
::v-deep .jira-import-table th {
  white-space: normal !important;
  line-height: 1.2;
  vertical-align: top !important;
}
::v-deep .jira-import-table td:first-child,
::v-deep .jira-import-table th:first-child {
  padding-left: 8px !important;
  padding-right: 8px !important;
}
::v-deep .jira-import-table tbody tr:nth-child(even) {
  background-color: rgba(0, 0, 0, 0.03);
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
